import { FC, useEffect, useState } from 'react';

import {
	ErrorMessage,
	Fieldset,
	Form,
	ImageContainer,
	Label,
	StyledFileDropzone
} from './InspirationItemDetailsModal-styles';
import Modal, { ModalActions, ModalBody } from '@/components/modal.tsx';
import RadioGroupController from '@/components/form/radio-group-controller.tsx';
import Button from '@/components/button.tsx';
import {
	Department,
	FlatInspirationItem,
	InspirationItemId,
	useCreateInspirationItemMutation,
	useGetInspirationItemQuery,
	useGetInspirationsMetadataQuery,
	useGetPresignedUrlMutation,
	usePatchInspirationItemMutation
} from '../../redux';
import { Loader } from '@/components/Loader.tsx';
import { ItemAttribute } from './components';

const initialFormState: FlatInspirationItem = {
	department: 'Womenswear',
	imageUrl: '',
	styles: [''],
	colors: [''],
	patterns: [''],
	fitsTop: [''],
	fitsBottom: [''],
	highlights: [''],
	heels: ''
};

interface InspirationItemDetailsModalProps {
	itemId?: InspirationItemId;
	action: 'add' | 'edit';
	onClose: () => void;
}

export const InspirationItemDetailsModal: FC<InspirationItemDetailsModalProps> = ({ itemId, action, onClose }) => {
	const [formState, setFormState] = useState<FlatInspirationItem>(initialFormState);
	const [error, setError] = useState<string | void>();
	const [previewImageUri, setPreviewImageUri] = useState<string | void>();
	const { data: item } = useGetInspirationItemQuery(itemId!, { skip: action === 'add' });
	const { data: metadata } = useGetInspirationsMetadataQuery(formState.department);
	const [createItem] = useCreateInspirationItemMutation();
	const [editItem] = usePatchInspirationItemMutation();
	const [getPresignedUrl] = useGetPresignedUrlMutation();

	const title = action === 'add' ? 'Add new inspiration photo' : 'Edit inspiration photo';
	const buttonLabel = action === 'add' ? 'Add Photo' : 'Save Changes';
	const isButtonDisabled = !!error || !formState.imageUrl || !formState.department;

	const uploadImage = async (file: File) => {
		const formData = new FormData();
		formData.append('picture', file, file.name);

		const { data } = await getPresignedUrl(formData);

		if (data) {
			const { uploadUrl, pictureUrl } = data;
			const fileReader = new FileReader();

			fileReader.onloadend = () => fileReader.result && setPreviewImageUri(fileReader.result as string);
			fileReader.readAsDataURL(file);

			const upload = await fetch(uploadUrl, {
				method: 'PUT',
				body: file
			});

			if (upload.status === 200) {
				setFormState((formState) => ({ ...formState, imageUrl: pictureUrl }));
				setError();
			} else {
				setError('Could not upload image');
			}
		} else {
			setError('Could not get presigned url');
		}
	};

	const submitForm = async () => {
		const itemData = {
			...formState,
			styles: formState.styles.filter(Boolean),
			colors: formState.colors.filter(Boolean),
			patterns: formState.patterns.filter(Boolean),
			fitsTop: formState.fitsTop.filter(Boolean),
			fitsBottom: formState.fitsBottom.filter(Boolean),
			highlights: formState.highlights.filter(Boolean),
			heels: formState.heels || undefined
		};

		try {
			setError();

			if (action === 'add') {
				await createItem(itemData).unwrap();
			} else {
				await editItem(itemData).unwrap();
			}
			onClose();
		} catch (e) {
			setError(`Failed to ${action} inspiration item`);
		}
	};

	useEffect(() => {
		if (item) {
			setFormState(item);
			setPreviewImageUri(item.imageUrl);
		}
	}, [item]);

	return (
		<Modal title={title} onClose={onClose}>
			<ModalBody>
				{metadata ? (
					<Form onSubmit={(event) => event.preventDefault()}>
						<ImageContainer>
							<StyledFileDropzone onFile={uploadImage}>
								{!!previewImageUri && <img src={previewImageUri} alt="" />}
							</StyledFileDropzone>
						</ImageContainer>

						<Fieldset>
							<Label>Department</Label>
							<RadioGroupController
								name="department"
								value={formState.department}
								onChange={(value) => setFormState({ ...formState, department: value as Department })}
								items={metadata.options.department}
							/>
						</Fieldset>
						<ItemAttribute
							label="Style"
							values={formState.styles}
							options={metadata.options.styles}
							onChange={(styles) =>
								setFormState({
									...formState,
									styles
								})
							}
						/>

						<ItemAttribute
							label="Colors"
							values={formState.colors}
							options={metadata.options.colors}
							onChange={(colors) =>
								setFormState({
									...formState,
									colors
								})
							}
						/>

						<ItemAttribute
							label="Patterns"
							values={formState.patterns}
							options={metadata.options.patterns}
							onChange={(patterns) =>
								setFormState({
									...formState,
									patterns
								})
							}
						/>

						<ItemAttribute
							label="Fit Top"
							values={formState.fitsTop}
							options={metadata.options.fitsTop}
							onChange={(fitsTop) =>
								setFormState({
									...formState,
									fitsTop
								})
							}
						/>

						<ItemAttribute
							label="Fit Bottom"
							values={formState.fitsBottom}
							options={metadata.options.fitsBottom}
							onChange={(fitsBottom) =>
								setFormState({
									...formState,
									fitsBottom
								})
							}
						/>

						<ItemAttribute
							label="Areas to highlight"
							values={formState.highlights}
							options={metadata.options.highlights}
							onChange={(highlights) =>
								setFormState({
									...formState,
									highlights
								})
							}
						/>

						<Fieldset>
							<Label>Heels</Label>
							<RadioGroupController
								name="heels"
								value={formState.heels}
								onChange={(value) =>
									setFormState({
										...formState,
										heels: value
									})
								}
								items={metadata.options.heels}
							/>
						</Fieldset>

						{!!error && (
							<Fieldset>
								<ErrorMessage>{error}</ErrorMessage>
							</Fieldset>
						)}

						<ModalActions>
							<Button $color="primary" disabled={isButtonDisabled} onClick={submitForm}>
								{buttonLabel}
							</Button>
						</ModalActions>
					</Form>
				) : (
					<Loader />
				)}
			</ModalBody>
		</Modal>
	);
};
